<template>
  <div
    v-if="!isReadOnly"
    class="rounded-md py-3"
    :class="[
      conditionType === 'or' ? '' : '',
      inner
        ? 'my-6 bg-stone-100/40 px-4 dark:bg-slate-900/90'
        : 'bg-transparent'
    ]">
    <template v-if="conditionGroups.length > 1">
      <div class="mb-6 dark:font-normal dark:text-white/70">
        <div v-if="changeType" class="flex items-center justify-between">
          <Select
            :modelValue="conditionType"
            @update:modelValue="updateConditionType($event)"
            optionLabel="label"
            optionValue="value"
            :options="[
              { label: 'At least one of the following is true', value: 'or' },
              { label: 'All of the following are true', value: 'and' }
            ]" />
          <Button
            @click="emits('remove:group')"
            label="Remove conditions group"
            severity="subtle"
            size="small"
            icon="pi pi-trash" />
        </div>
        <div v-else class="font-bold">
          At least one of the following are true:
        </div>
      </div>
    </template>

    <div class="">
      <!-- Condition groups (renders auto-sync badge via recursive isReadOnly detection) -->
      <template v-if="conditionGroups.length === 0 && (hasUserInteracted || inner)">
        <div class="my-4 rounded-md bg-stone-200/30 p-4 dark:bg-slate-900/90">
          <p class="text-center text-stone-600 dark:text-white/70">
            No conditions added yet
          </p>
        </div>
      </template>
      <template v-else-if="conditionGroups.length > 0">
        <div v-for="(group, index) in conditionGroups" :key="index">
          <div
            v-if="readableLogic && index > 0"
            class="my-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-white/50"
          >
            <div class="h-px flex-1 bg-slate-300/60 dark:bg-slate-700/80" />
            <span>{{ conditionType.toUpperCase() }}</span>
            <div class="h-px flex-1 bg-slate-300/60 dark:bg-slate-700/80" />
          </div>
          <div
            :class="{
              'mb-4 border-b border-slate-300/50 pb-4': inner && !readableLogic
            }">
            <template v-if="group.and || group.or">
              <template v-if="shouldDeferInnerGroups">
                <Button
                  class="w-full justify-start text-left"
                  size="small"
                  severity="secondary"
                  text
                  :label="deferredGroupSummaryLabel(group)"
                  :aria-expanded="expandedDeferredGroupIndex === index"
                  :aria-controls="deferredGroupId(index)"
                  @click="toggleDeferredGroup(index)"
                />
                <div
                  v-if="expandedDeferredGroupIndex === index"
                  :id="deferredGroupId(index)"
                >
                  <ConditionGroup
                    @remove:group="removeGroup(index)"
                    @condition:updated="emits('condition:updated', $event)"
                    :changeType="true"
                    :inner="true"
                    :condition="group"
                    :source-fields="sourceFields"
                    :form-schema="formSchema"
                    :readable-logic="readableLogic" />
                </div>
              </template>
              <template v-else>
                <ConditionGroup
                  @remove:group="removeGroup(index)"
                  @condition:updated="emits('condition:updated', $event)"
                  :changeType="true"
                  :inner="true"
                  :condition="group"
                  :source-fields="sourceFields"
                  :form-schema="formSchema"
                  :readable-logic="readableLogic" />
              </template>
            </template>
            <template v-else>
              <Condition
                :condition="group"
                :source-fields="sourceFields"
                :form-schema="formSchema"
                @update:condition="updateCondition(index, $event)"
                @delete:condition="removeCondition(index)" />
            </template>
          </div>
        </div>
      </template>
      <!-- AI inline input: shows below auto-sync badge when no user-defined conditions -->
      <template v-if="userDefinedConditionCount === 0 && !inner && !hasUserInteracted">
        <div class="my-4">
          <RightyInline
            ref="aiInlineRef"
            taskType="generate_conditions"
            placeholder="People who visited /pricing in the last week but are not on my email list"
            buttonLabel="Generate Conditions"
            :context="aiContext"
            :showResponse="false"
            minHeight="5rem"
            @result="handleAiResult"
          />
        </div>
      </template>
      <div v-if="inner" class="mt-4 flex space-x-4 pb-1">
        <Button
          @click="addCondition"
          :label="`New '${conditionType}' condition`"
          size="small"
          icon="pi pi-plus"
          severity="subtle" />
      </div>
      <div v-else class="mt-4 flex space-x-4">
        <Button
          @click="addGroup"
          :label="userDefinedConditionCount > 0 ? 'Add conditions' : 'Add conditions manually'"
          size="small"
          severity="subtle"
          icon="pi pi-plus" />
        <RightyButton
          v-if="userDefinedConditionCount > 0"
          label="AI Generate"
          size="small"
          @click="showAiModal = true"
        />
      </div>
    </div>
  </div>
  <div v-else>
    <div
      v-if="syncDefined"
      class="my-4 rounded-md bg-stone-200/50 px-5 py-4 dark:bg-slate-900/90 dark:text-white/70">
      <div class="flex items-center gap-x-3">
        <LoopIcon class="h-4 w-4" />
        <div>
          <Tag severity="info">Auto synced</Tag>
          <template v-if="syncConditionType === 'tags'">
            If tagged <strong>{{ syncTagLabel }}</strong>
          </template>
          <template v-else>
            If the field
            <strong>{{ customField }}</strong>
            equals "{{ props.condition.and?.[0]?.value }}"
          </template>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    :visible="showAiModal"
    modal
    :closable="true"
    :style="{ width: '50rem' }"
    @update:visible="showAiModal = $event"
    appendTo="body"
  >
    <template #header>
      <h3 class="text-lg font-semibold">
        Generate Conditions with AI
      </h3>
    </template>
    <RightyInline
      ref="aiModalRef"
      class="mt-4"
      taskType="generate_conditions"
      placeholder="Add a condition for visitors from the US, or remove the pageview condition"
      buttonLabel="Generate Conditions"
      :context="aiContextWithExisting"
      :showResponse="false"
      @result="handleAiResult"
    />
  </Dialog>
</template>

<script setup>
import { computed, ref } from 'vue'
import RightyInline from '@/modules/righty/components/RightyInline.vue'
import RightyButton from '@/components/common/RightyButton.vue'
import { flushPendingDebouncedFields } from '@/composables/useDebouncedField'
import { useToast } from 'primevue/usetoast'
import { validateLogicDefinition, autoFixCondition } from '@/modules/righty/services/conditionValidator'
import { countLogicDefinitionConditions } from '@/modules/segment/services/segmentUtilities'

const toast = useToast()

const props = defineProps({
  condition: Object,
  skipIndex: { type: Number, default: null },
  readonly: { type: Boolean, default: false },
  inner: { type: Boolean, default: false },
  changeType: { type: Boolean, default: true },
  readableLogic: { type: Boolean, default: false },
  deferInnerGroups: { type: Boolean, default: false },
  deferredGroupIdPrefix: { type: String, default: '' },
  sourceFields: { type: Array, default: null },
  formSchema: { type: Array, default: null },
  usageContext: { type: String, default: 'segment signal auto-segmentation' }
})

const integrationStore = useIntegrationStore()
const { partnerData, integratedPartner } = storeToRefs(integrationStore)

const emits = defineEmits(['remove:group', 'condition:updated'])
const segmentGroup = inject('segmentGroup', null)

const conditionType = computed(() => Object.keys(props.condition)[0])

const conditionGroups = computed(() => {
  const conditions = props.condition[conditionType.value]
  if (props.skipIndex !== null) {
    return conditions.filter((_, index) => index !== props.skipIndex)
  }
  return conditions
})

const shouldDeferInnerGroups = computed(() => props.deferInnerGroups && !props.inner)
const expandedDeferredGroupIndex = ref(null)

const deferredGroupId = (index) => `${props.deferredGroupIdPrefix}-condition-group-${index}`

const deferredGroupSummaryLabel = (group) => {
  const conditionCount = countLogicDefinitionConditions(group)

  if (conditionCount === 0) return 'Empty condition group'

  const prefix = group.and ? 'All' : 'Any'
  const suffix = conditionCount === 1 ? 'condition' : 'conditions'
  return `${prefix} of ${conditionCount} ${suffix}`
}

const setExpandedDeferredGroupIndex = (index) => {
  if (
    expandedDeferredGroupIndex.value !== null &&
    expandedDeferredGroupIndex.value !== index
  ) {
    flushPendingDebouncedFields()
  }

  expandedDeferredGroupIndex.value = index
}

const toggleDeferredGroup = (index) => {
  if (!shouldDeferInnerGroups.value) return

  setExpandedDeferredGroupIndex(
    expandedDeferredGroupIndex.value === index ? null : index
  )
}

// Sync-managed conditions are identified by the explicit `$managedBy: 'sync'`
// marker on the condition object. The marker is applied on sync setup/repair
// by repairSegmentGroup() and is the canonical ownership signal — shape-based
// matching (same $type + customFieldId) incorrectly flagged user-authored
// rules that happened to share those fields (ENG-1009).
const isSyncManaged = (condition) =>
  condition?.and?.length === 1 &&
  condition?.and?.[0]?.$managedBy === 'sync'

// Index of the auto-sync condition in the outer condition groups (only at top
// level). Uses the $managedBy marker so it's mode-agnostic — previously this
// was gated on syncToCustomField, which broke tag-mode sync (no $managedBy
// detection → userDefinedConditionCount double-counted the sync condition).
const autoSyncIndex = computed(() => {
  if (props.inner) return -1
  const or = props.condition?.or
  if (!Array.isArray(or)) return -1
  return or.findIndex((group) => isSyncManaged(group))
})

// Count of user-defined conditions (excludes auto-sync)
const userDefinedConditionCount = computed(() => {
  const total = conditionGroups.value?.length || 0
  return autoSyncIndex.value >= 0 ? total - 1 : total
})

// True when sync is actively configured for this segment group. Covers both
// modes: field-mode requires syncToCustomField; tag-mode requires at least
// one segment tag mapping.
const hasActiveSync = computed(() => {
  const sg = segmentGroup?.value
  if (!sg) return false
  if (sg.syncMode === 'tag') {
    return Object.keys(sg.syncMappings || {}).length > 0
  }
  return !!sg.syncToCustomField
})

const isReadOnly = computed(() => isSyncManaged(props.condition))

const syncDefined = computed(() => isSyncManaged(props.condition))

// TODO: Refactor
const customField = computedAsync(async () => {
  if (integratedPartner?.value?.espFeatureSupport?.customField?.unstructured) {
    return segmentGroup.value.syncToCustomField
  }
  return (await getIntegrationData('partner', 'custom_fields')).value.find(
    (field) => String(field.id) === segmentGroup.value.syncToCustomField
  )?.label
}, '...')

// The sync condition's $type — drives how the Auto synced card is rendered
// (field-mode vs tag-mode wording + label source).
const syncConditionType = computed(
  () => props.condition?.and?.[0]?.$type
)

// Resolve the human-readable label for a tag-mode sync condition by looking
// up the mapped tagId in the integration's tag data. Falls back to the raw
// tag ID if the tag is unknown (deleted in ESP but still referenced locally).
const syncTagLabel = computedAsync(async () => {
  const tagIds = props.condition?.and?.[0]?.tagIds
  if (!Array.isArray(tagIds) || tagIds.length === 0) return '...'
  const tagId = String(tagIds[0])
  const tags = (await getIntegrationData('partner', 'tags')).value
  return tags.find((t) => String(t.id) === tagId)?.label || tagId
}, '...')

const addGroup = () => {
  hasUserInteracted.value = true
  props.condition[conditionType.value].push({ and: [{}] })

  if (shouldDeferInnerGroups.value) {
    setExpandedDeferredGroupIndex(props.condition[conditionType.value].length - 1)
  }
}

const removeGroup = (index) => {
  if (shouldDeferInnerGroups.value && expandedDeferredGroupIndex.value !== null) {
    flushPendingDebouncedFields()
  }

  const nextExpandedDeferredGroupIndex =
    expandedDeferredGroupIndex.value === null || expandedDeferredGroupIndex.value < index
      ? expandedDeferredGroupIndex.value
      : expandedDeferredGroupIndex.value === index
        ? null
        : expandedDeferredGroupIndex.value - 1

  props.condition[conditionType.value].splice(index, 1)
  expandedDeferredGroupIndex.value = nextExpandedDeferredGroupIndex

  // Reset latch when user explicitly removes all user-defined conditions,
  // so the AI inline input reappears. Safe because config-refetch race
  // conditions don't call removeGroup — they reactively replace the object.
  if (userDefinedConditionCount.value === 0) {
    hasUserInteracted.value = false
  }
}

const addCondition = () => {
  hasUserInteracted.value = true
  props.condition[conditionType.value].push({})
  
  // Emit condition update event for new condition
  emits('condition:updated', {
    action: 'add',
    conditionType: conditionType.value,
    index: props.condition[conditionType.value].length - 1
  })
}

const removeCondition = (index) => {
  const removedCondition = props.condition[conditionType.value][index]
  props.condition[conditionType.value].splice(index, 1)
  
  // Emit condition update event for removed condition
  emits('condition:updated', {
    action: 'remove',
    removedCondition,
    conditionType: conditionType.value,
    index
  })
  
  if (props.condition[conditionType.value].length === 0) {
    emits('remove:group')
  }
}

const updateConditionType = (value) => {
  const oldValue = conditionType.value
  const newCondition = {}
  newCondition[value] = conditionGroups.value
  delete props.condition[oldValue]
  props.condition[value] = newCondition[value]
  
  // Emit condition type change event
  emits('condition:updated', {
    action: 'typeChange',
    oldType: oldValue,
    newType: value,
    conditions: conditionGroups.value
  })
}

const updateCondition = (index, newCondition) => {
  const conditions = props.condition[conditionType.value]
  const updateIndex = props.skipIndex !== null ? index + 1 : index
  const oldCondition = conditions[updateIndex]

  // Only assign if it's actually a different object reference
  // This prevents unnecessary Vue reactivity triggers on every keystroke
  // (the emitted newCondition is typically the same object as conditions[updateIndex])
  if (oldCondition !== newCondition) {
    conditions[updateIndex] = newCondition
  }

  // Emit condition update event with details
  emits('condition:updated', {
    oldCondition,
    newCondition,
    index: updateIndex,
    conditionType: conditionType.value
  })
}

// Track if user has interacted with conditions in this component instance.
// Prevents the AI inline input from reappearing after a config save/refetch
// race condition resets conditionGroups.length to 0 momentarily.
// Also true if user-defined conditions already exist on mount (excludes auto-sync).
const hasUserInteracted = ref(userDefinedConditionCount.value > 0)

// On cold page load, integratedPartner may not be available yet, causing
// autoSyncIndex to return -1 and hasUserInteracted to latch true prematurely.
// Watch for integration data to load and correct the latch if needed. Uses
// hasActiveSync so it fires for both field-mode (syncToCustomField set) and
// tag-mode (syncMappings populated) sync configurations.
if (!props.inner && hasActiveSync.value && autoSyncIndex.value === -1) {
  const unwatch = watch(autoSyncIndex, (newIndex) => {
    if (newIndex >= 0 && userDefinedConditionCount.value === 0) {
      hasUserInteracted.value = false
    }
    unwatch()
  })
}

watch(
  () => conditionGroups.value.length,
  (length) => {
    if (expandedDeferredGroupIndex.value !== null && expandedDeferredGroupIndex.value >= length) {
      expandedDeferredGroupIndex.value = null
    }
  }
)

// AI Generate state
const showAiModal = ref(false)
const aiInlineRef = ref(null)
const aiModalRef = ref(null)

const aiContext = computed(() => ({
  usageContext: props.usageContext
}))

const aiContextWithExisting = computed(() => ({
  existingConditions: props.condition,
  usageContext: props.usageContext
}))

const applyConditionDefinition = (definition) => {
  const newType = Object.keys(definition)[0]
  const newGroups = definition[newType]

  if (!newType || !Array.isArray(newGroups)) return false

  // Auto-fix each leaf condition
  const fixGroups = (groups) => {
    return groups.map(group => {
      if (group.and) {
        return { and: group.and.map(c => (c.$source ? autoFixCondition(c, { strictPageviewPaths: true }) : c)) }
      }
      if (group.or) {
        return { or: group.or.map(c => (c.$source ? autoFixCondition(c, { strictPageviewPaths: true }) : c)) }
      }
      if (group.$source) {
        return autoFixCondition(group, { strictPageviewPaths: true })
      }
      return group
    })
  }

  const fixedGroups = fixGroups(newGroups)

  // Validate before applying
  const errors = validateLogicDefinition({ [newType]: fixedGroups }, { strictPageviewPaths: true })
  if (errors.length > 0) {
    console.warn('Condition validation warnings:', errors)
    // Continue anyway - validation is advisory, conditions may reference dynamic IDs
  }

  // Replace the condition content
  const currentType = conditionType.value
  props.condition[currentType].splice(0)

  if (newType !== currentType) {
    delete props.condition[currentType]
    props.condition[newType] = fixedGroups
  } else {
    props.condition[currentType].push(...fixedGroups)
  }

  return true
}

const handleAiResult = (result) => {
  if (!result.success) {
    toast.add({
      severity: 'error',
      summary: 'Generation Failed',
      detail: result.error || 'Failed to generate conditions',
      life: 5000
    })
    return
  }

  const definition = result.data
  if (!definition) {
    toast.add({
      severity: 'error',
      summary: 'Generation Failed',
      detail: 'No conditions were generated',
      life: 5000
    })
    return
  }

  hasUserInteracted.value = true
  const applied = applyConditionDefinition(definition)
  if (!applied) {
    toast.add({
      severity: 'error',
      summary: 'Invalid Format',
      detail: 'The generated conditions had an unexpected format',
      life: 5000
    })
    return
  }

  // Capture the user's instruction before clearing
  const instruction = aiModalRef.value?.instructions || aiInlineRef.value?.instructions || ''

  // Close modal and clear inputs
  showAiModal.value = false
  aiModalRef.value?.clearInput()
  aiInlineRef.value?.clearInput()

  toast.add({
    severity: 'success',
    summary: 'Conditions Generated',
    detail: 'AI has generated the targeting conditions',
    life: 3000
  })

  const groups = definition[Object.keys(definition)[0]] || []
  const totalConditions = countLogicDefinitionConditions(definition)
  const isComplex = totalConditions >= 3 || groups.length >= 2

  emits('condition:updated', {
    action: 'ai_generated',
    instruction,
    isComplex
  })
}
</script>
